import moment from 'moment';
import * as timeHelper from './dateTimeHelper';

export function parseLoungeHoursMap(locale, openHoursMap, loungeTimeZone) {
  if (typeof openHoursMap.monday[0].open === 'string') {
    const dateInTimeZone = timeHelper
      .getCurrentTimeForTimeZone(locale, loungeTimeZone)
      .format('DDMMYYYY');
    Object.keys(openHoursMap).forEach((day) => {
      const openTimeDate = `${openHoursMap[day][0].open} ${dateInTimeZone}`;
      const closeTimeDate = `${openHoursMap[day][0].close} ${dateInTimeZone}`;
      const lastOpenTimeDate = `${openHoursMap[day][1]?.open} ${dateInTimeZone}`;
      const lastCloseTimeDate = `${openHoursMap[day][1]?.close} ${dateInTimeZone}`;
      Object.assign(openHoursMap[day], {
        0: {
          open: timeHelper.parseTimeDateStringToMoment(
            openTimeDate,
            loungeTimeZone
          ),
          close: timeHelper.parseTimeDateStringToMoment(
            closeTimeDate,
            loungeTimeZone
          ),
          note: openHoursMap[day][0].note,
        },
        ...(openHoursMap[day].length > 1 && {
          1: {
            open: timeHelper.parseTimeDateStringToMoment(
              lastOpenTimeDate,
              loungeTimeZone
            ),
            close: timeHelper.parseTimeDateStringToMoment(
              lastCloseTimeDate,
              loungeTimeZone
            ),
            note: openHoursMap[day][1].note,
          },
        }),
      });
    });
  }
  return openHoursMap;
}

export function getLoungeOpenTimesForToday(locale, lounge) {
  const currentDay = timeHelper.getCurrentDayForTimeZone(
    /*
      our API returns a map whose keys are day names
      written in english so the "currentDay" of the week needs
      to be in english to find the hours in the data
    */
    'en-us',
    lounge.airport.time_zone
  );

  return lounge?.hours?.[currentDay]?.[0];
}

export function getAllLoungeOpenTimesForToday({ lounge }) {
  /*
      our API returns a map whose keys are day names
      written in english so the "currentDay" of the week needs
      to be in english to find the hours in the data
  */
  const currentDay = timeHelper.getCurrentDayForTimeZone(
    'en-us',
    lounge.airport.time_zone
  );

  return lounge?.hours?.[currentDay];
}

export function getLoungeOpenTimesForTodayAsString(locale, lounge, format) {
  const hoursForToday = getLoungeOpenTimesForToday(locale, lounge);
  if (hoursForToday === undefined || hoursForToday === null) {
    return null;
  }
  const openTimeString = timeHelper.parseMomentToString(
    hoursForToday.open,
    format
  );
  const closeTimeString = timeHelper.parseMomentToString(
    hoursForToday.close,
    format
  );
  return `${openTimeString} - ${closeTimeString}`;
}

export function isLoungeTempClosed(lounge) {
  return lounge.name.toLocaleLowerCase().includes('temporarily closed');
}

export function isLoungeOpen(locale, lounge) {
  const loungeTimeZone = lounge.airport.time_zone;
  const loungeLocalTime = timeHelper.getCurrentTimeForTimeZone(
    locale,
    loungeTimeZone
  );
  const loungeTimes = getAllLoungeOpenTimesForToday({ lounge });

  if (isLoungeTempClosed(lounge)) {
    // Lounges close temporarily for maintenance or other events.
    // Design/Product team want to show closed text rather than nothing.
    // There isn't a "temporarily closed" property, they just updated the lounge name and sometimes the hours.
    return false;
  }

  return loungeTimes?.some(({ open, close }) => {
    const openLoungeTime = moment(open);
    const closeLoungeTime = moment(close);

    if (!(openLoungeTime.isValid() && closeLoungeTime.isValid())) {
      return null;
    }
    if (timeHelper.isBeforeOrEqual(closeLoungeTime, openLoungeTime)) {
      closeLoungeTime.add(1, 'd');
    }
    /* If the time goes to the next day, the moment object for loungeTimes is for the next day,
  so the moment object for the local time needs to be updated */
    if (timeHelper.isUsingNextDayTimes(loungeLocalTime, openLoungeTime)) {
      loungeLocalTime.add(1, 'd');
    }

    return timeHelper.isBetweenTimes(
      loungeLocalTime,
      openLoungeTime,
      closeLoungeTime
    );
  });
}

export function generateLoungeHoursAsString(locale, lounge, format) {
  const loungeTimes = getLoungeOpenTimesForToday(locale, lounge);
  const openLoungeTime = moment(loungeTimes.open);
  const closeLoungeTime = moment(loungeTimes.close);
  const isValidLoungeTime =
    openLoungeTime.isValid() && closeLoungeTime.isValid();

  if (isValidLoungeTime) {
    const openTimeString = timeHelper.parseMomentToString(
      openLoungeTime,
      format
    );
    const closeTimeString = timeHelper.parseMomentToString(
      closeLoungeTime,
      format
    );
    return `${openTimeString} - ${closeTimeString}`;
  }
  return loungeTimes.note;
}

export function isNoteInsteadOfOpenCloseTimes(openHoursMap) {
  try {
    const openMoment = openHoursMap.monday[0].open;
    const closeMoment = openHoursMap.monday[0].open;
    if (openMoment instanceof moment && closeMoment instanceof moment) {
      return false;
    }
    return true;
  } catch (err) {
    throw new Error(
      'Issue traversing lounge hours. Check if the lounge object passed has the correct format.'
    );
  }
}

export function generateLoungeUrl(loungeName, loungeTerminal, loungeId) {
  const name = loungeName.replace(/[^\d+a-z-]+/gi, '-');
  const terminal = loungeTerminal.replace(/[^\d+a-z-]+/gi, '-');
  return `${name}-${terminal}-${loungeId}`;
}

export function prioritiseAccessRules(accessRulesList, providers) {
  return accessRulesList.reduce((prioritisedAccessRules, accessRule) => {
    const providerId = accessRule.provider_id;
    const matchedProvider = providers.find(
      (provider) =>
        provider.provider_id &&
        providerId &&
        provider.provider_id.toUpperCase().includes(providerId.toUpperCase())
    );
    const tmpAccessRule = accessRule;
    if (matchedProvider) {
      tmpAccessRule.order = matchedProvider.order;
    } else {
      tmpAccessRule.order = 9;
    }
    prioritisedAccessRules.push(tmpAccessRule);
    return prioritisedAccessRules.sort((a, b) => a.order - b.order);
  }, []);
}

export function getPrioritisedProviderForLounge(locale, lounge, providers) {
  Object.assign(lounge, {
    hours: parseLoungeHoursMap(locale, lounge.hours, lounge.airport.time_zone),
  });
  if (lounge.access_rules?.length === 0) {
    return null;
  }
  const providerId = prioritiseAccessRules(lounge.access_rules, providers)[0]
    .provider_id;
  return providers.find(
    (provider) =>
      provider.provider_id &&
      providerId &&
      provider.provider_id.toUpperCase().includes(providerId.toUpperCase())
  );
}

export function prioritiseLounges(locale, loungeList, providers) {
  return loungeList.reduce((prioritisedLounges, lounge) => {
    const matchedProvider = getPrioritisedProviderForLounge(
      locale,
      lounge,
      providers
    );
    if (matchedProvider) {
      const priorityLounge = lounge;
      priorityLounge.group = matchedProvider.order;
      prioritisedLounges.push(priorityLounge);
    } else {
      const nonPriorityLounge = lounge;
      nonPriorityLounge.group = 9;
      prioritisedLounges.push(nonPriorityLounge);
    }
    return prioritisedLounges.sort(
      (a, b) => a.group - b.group || a.name > b.name
    );
  }, []);
}
